// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
import { APPLICABLE_MODULES, REGULATORY_FRAMEWORK_STATUSES } from './localization.js';
/** All Sector values, in display order. */
export const SECTOR_VALUES = [
    'banking',
    'insurance',
    'healthcare',
    'education',
    'ict_critical',
    'telecom',
    'energy',
    'transport',
    'digital_services',
    'digital_technology_industry',
    'manufacturing',
    'public_administration',
    'payments',
    'publishing',
    'crypto_assets',
    'other',
];
export function isSector(value) {
    return typeof value === 'string' && SECTOR_VALUES.includes(value);
}
/**
 * Sprint 62.58.0 — the declared vocabulary of a guidance note's `status`.
 *
 * A guidance note carries the status of the instrument it explains, so the
 * vocabulary is the framework status vocabulary (`REGULATORY_FRAMEWORK_STATUSES`,
 * read rather than restated) plus the two states that belong to a note and not
 * to a framework: `deprecated` (the note itself is withdrawn) and `superseded`
 * (another note replaces it; a framework expresses that by `replaced_by`).
 *
 * The union used to be four values while the corpus carried seven — `upcoming`,
 * `legacy` and `temporary_active` among them — and one record read
 * `practical_guidance`, a guidance type written into the status field. Nothing
 * refused it: `pack-schemas.ts` has no guidance schema and the installer copies
 * `status` as written. `scripts/validate-pack-content.ts` now refuses a
 * guidance whose status is outside this list (`GUIDANCE_STATUS_UNDECLARED`).
 *
 * Measured 2026-11-01 over every `guidances.json` under `packs/`: active 414, draft 40,
 * upcoming 26, legacy 19, superseded 3, temporary_active 2. `deprecated` and
 * `active_reference` are declared and carried by no note today.
 */
export const GUIDANCE_STATUSES = [
    ...REGULATORY_FRAMEWORK_STATUSES,
    'deprecated',
    'superseded',
];
/** True when `value` is a member of `GUIDANCE_STATUSES`. */
export function isGuidanceStatus(value) {
    return typeof value === 'string' && GUIDANCE_STATUSES.includes(value);
}
/**
 * Sprint 62.58.0 — the spelling of a guidance note's `guidance_type`, `category`
 * and `related_modules` value: lower-case snake_case, as every `ModuleId`,
 * `ApplicableModule` and `GuidanceType` is written. `validate:packs` refuses a
 * value of another shape (`GUIDANCE_MODULE_ID_VARIANT`, `GUIDANCE_ENUM_ID_SHAPE`).
 */
export const GUIDANCE_ENUM_ID_PATTERN = /^[a-z][a-z0-9]*(?:_[a-z0-9]+)*$/;
/**
 * Sprint 62.58.0 — a guidance type, category or module value read as one id:
 * trimmed, lower-cased, spaces and hyphens read as underscores, repeated
 * underscores collapsed and leading or trailing ones dropped. `''` for a value
 * that is not a string or has nothing left.
 *
 * The reading Framework Detail's Guidance tab gives a value before it looks up
 * a label or builds a filter option, and the reading `validate:packs` offers an
 * author as the spelling to write. It invents nothing: a value with characters
 * outside `[a-z0-9_]` stays as it is, and the validator says so.
 */
export function normaliseGuidanceEnumId(raw) {
    if (typeof raw !== 'string')
        return '';
    return raw
        .trim()
        .toLowerCase()
        .replace(/[\s-]+/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_+|_+$/g, '');
}
const DECLARED_APPLICABLE_MODULE_IDS = new Set(APPLICABLE_MODULES);
/**
 * Sprint 62.58.0 — a guidance note's `related_modules` value as the module it
 * names: `normaliseGuidanceEnumId`, then the plural of a declared
 * `APPLICABLE_MODULES` member read as that member (`rights_requests` →
 * `rights_request`, `incidents` → `incident`). The plurals are `ModuleId`
 * toggle ids, which name a product module rather than the module a note
 * applies to; older pack versions wrote them. A value outside the list that is
 * not such a plural (`audit_trail`) is returned normalised, unchanged.
 *
 * One rule, read by both of its readers: `validate:packs`
 * (`GUIDANCE_MODULE_ID_VARIANT` refuses a value this changes) and the Guidance
 * tab, which reads a tenant's installed notes as written — a tenant that has
 * not reinstalled a pack still holds the older spelling.
 */
export function canonicalGuidanceModuleId(raw) {
    const id = normaliseGuidanceEnumId(raw);
    if (DECLARED_APPLICABLE_MODULE_IDS.has(id))
        return id;
    const singular = id.endsWith('s') ? id.slice(0, -1) : '';
    return singular && DECLARED_APPLICABLE_MODULE_IDS.has(singular) ? singular : id;
}
/**
 * Sprint 62.58.0 — guidance type values older pack versions wrote, each with the
 * `GuidanceType` the platform now writes for it. There is no declared guidance
 * type vocabulary beyond the `GuidanceType` union (the corpus carries 43
 * values), so a re-coded type has nothing to fold to unless it is named here.
 *
 * Installed guidance rows keep the spelling of the pack version that wrote them
 * until the pack is reinstalled (the installer upserts with `$set`), so the
 * Guidance tab reads an alias as its canonical type — one filter option, the
 * canonical label — and `validate:packs` refuses an alias in a pack
 * (`GUIDANCE_ENUM_ID_SHAPE`), naming the spelling to write. Add an entry when a
 * pack re-codes a type; never remove one while an installed tenant may hold it.
 */
export const GUIDANCE_TYPE_ALIASES = {
    /** Re-coded 2026-11-01 (Sprint 62.58.0) to the `interpretive_note` the rest of the corpus carries. */
    interpretation_note: 'interpretive_note',
};
/** The canonical type an alias stands for, or `null` when `id` is not an alias. */
export function guidanceTypeAliasTarget(id) {
    return Object.prototype.hasOwnProperty.call(GUIDANCE_TYPE_ALIASES, id)
        ? GUIDANCE_TYPE_ALIASES[id]
        : null;
}
/**
 * Sprint 62.58.0 — a guidance note's `guidance_type` as one id:
 * `normaliseGuidanceEnumId`, then a `GUIDANCE_TYPE_ALIASES` value read as the
 * type it stands for.
 */
export function canonicalGuidanceTypeId(raw) {
    const id = normaliseGuidanceEnumId(raw);
    return guidanceTypeAliasTarget(id) ?? id;
}
/**
 * Sprint 62.58.0 (walk D-1) — the `?status=` value that asks
 * `GET /localization/frameworks/:code/guidances` for notes of EVERY status.
 *
 * The list defaults to `active` when no status is given, and that default stays:
 * Forseti's framework reading and any caller that names no status keep reading
 * active notes only. Framework Detail asks for this value instead, because it
 * decides whether to show its Guidance tab from the answer — and seven packs
 * (the vehicle-monitoring draft among them) install notes that are all `draft`
 * or `upcoming`, so an active-only answer hid every note they declare.
 */
export const GUIDANCE_LIST_ALL_STATUSES = 'all';
// ── Sprint 62.1.0 — Typed framework relationships and overlay runtime semantics ──
/**
 * Sprint 62.1.0 — kinds of typed relationship between two frameworks.
 *
 * Before this sprint the only framework linkage was the scalar
 * `parent_framework_code`, plus an authored-but-unresolved
 * `applies_to_frameworks` array. That made it impossible for one optional
 * overlay to appear beneath every applicable parent, and impossible for a
 * child instrument to inherit its parent's optional/overlay treatment.
 *
 * `parent` retains scalar compatibility during migration; the remaining kinds
 * are new and are what the relationship graph resolves.
 */
export const FRAMEWORK_RELATIONSHIP_KINDS = [
    /** Source is the structural child of target (migrated from `parent_framework_code`). */
    'parent',
    /** Source overlay applies to target framework (migrated from `applies_to_frameworks`). */
    'applies_to',
    /** Source is a non-hierarchical cross reference to target. */
    'cross_reference',
    /** Source supersedes target; target becomes legacy and is hidden by default. */
    'supersedes',
];
export function isFrameworkRelationshipKind(value) {
    return (typeof value === 'string' && FRAMEWORK_RELATIONSHIP_KINDS.includes(value));
}
/** The three API-reported kinds, in the order a page states them. */
export const DECLARATION_ABSENCE_KINDS = [
    'none_declared',
    'reference_mode',
    'not_operational',
];
//# sourceMappingURL=regulatory.js.map